import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DriverService } from '@app/services/driver.service';
import { MediaService } from '@app/services/media.service';
import { formatDateString } from '@app/shared/helpers/date.helper';

@Component({
  selector: 'app-step-driver-info',
  templateUrl: './step-driver-info.component.html',
  styleUrls: ['./step-driver-info.component.scss'],
  standalone: false,
})
export class StepDriverInfoComponent implements OnInit {
  @Input() driverInfoForm!: FormGroup;
  @Input() preloadedData?: any;

  driverData!: any;

  selectedOrganization: any;
  isDriverSearchEnabled: boolean = false;
  showDriverSearch: boolean = false;
  showPreloadedDriver: boolean = false;
  preloadedDriverCode: string = '';
  preloadedDriverDisplayText: string = '';
  isLoadingPreloadedDriver: boolean = false;

  assignee: any;

  constructor(private driverService: DriverService, private mediaService: MediaService) {}

  ngOnInit(): void {
    this.loadSelectedOrganization();
    this.driverInfoForm.get('organization')?.setValue(this.selectedOrganization);

    // Check if there's preloaded driver data (from Add New button)
    if (this.preloadedData?.driverGUID) {
      this.loadPreloadedDriverData();
    } else {
      this.showDriverSearch = true;
      this.isDriverSearchEnabled = true;
    }
  }

  loadPreloadedDriverData(): void {
    // Show preloaded driver display instead of search
    this.showDriverSearch = false;
    this.showPreloadedDriver = true;
    this.isDriverSearchEnabled = false;
    this.isLoadingPreloadedDriver = true;

    if (this.preloadedData?.driverGUID && this.selectedOrganization) {
      this.driverService.getDriverProfile(this.preloadedData.driverGUID).subscribe({
        next: (driverData: any) => {
          this.isLoadingPreloadedDriver = false;
          if (driverData && driverData.length > 0) {
            const driver = driverData[0];
            this.assignee = driver;

            this.preloadedDriverCode = driver.driverId;
            this.preloadedDriverDisplayText = `${this.capitalizeWords(
              driver.driverName
            )} - ${driver.driverId}`;

            this.handleDriverSelected(driver);
          } else {
            console.error('Driver not found with GUID:', this.preloadedData.driverGUID);
            this.showPreloadedDriver = false;
            this.showDriverSearch = true;
            this.isDriverSearchEnabled = true;
          }
        },
        error: (error) => {
          this.isLoadingPreloadedDriver = false;
          console.error('Error loading preloaded driver data:', error);
          this.showPreloadedDriver = false;
          this.showDriverSearch = true;
          this.isDriverSearchEnabled = true;
        },
      });
    }
  }

  loadSelectedOrganization(): void {
    const storedOrganization = localStorage.getItem('selectedOrganization');
    if (storedOrganization) {
      this.selectedOrganization = JSON.parse(storedOrganization);
    }
  }

  onOrganizationChange(newOrganization: any): void {
    this.selectedOrganization = newOrganization.value;
    this.driverInfoForm.get('organization')?.setValue(this.selectedOrganization);
    this.isDriverSearchEnabled = true;

    // Reset driver fields when the organization changes
    this.driverInfoForm.get('driverCode')?.reset();
    this.handleDriverSelected(null);
    this.assignee = null;
  }

  private parseManagers(managersString: string): { [key: string]: string } {
    const managerMap: { [key: string]: string } = {};
    const managerEntries = managersString.split(',');

    managerEntries.forEach((entry) => {
      const [role, name] = entry.split(':').map((part) => part.trim());
      if (role && name) {
        managerMap[role] = name;
      }
    });

    return managerMap;
  }

  handleDriverSelected(driver: any): void {
    if (driver) {
      this.driverData = driver;

      this.driverInfoForm.get('cellphone')?.setValue(this.driverData.phone);
      this.driverInfoForm.get('driverName')?.setValue(this.driverData?.driverName);
      this.driverInfoForm.get('driverId')?.setValue(this.driverData?.id);
      this.driverInfoForm.get('driverCode')?.setValue(this.driverData?.driverId);
      this.driverInfoForm.get('terminal')?.setValue(this.driverData?.terminal);
      this.driverInfoForm
        .get('driverHireDate')
        ?.setValue(
          this.driverData?.hireDate ? formatDateString(this.driverData.hireDate) : ''
        );

      let managerMap: { [key: string]: string } = {};

      // Handle different possible manager data formats
      // Check for managers (lowercase 'm')
      if (typeof this.driverData.managers === 'string') {
        // Check if it's a JSON string
        if (
          this.driverData.managers.trim().startsWith('[') ||
          this.driverData.managers.trim().startsWith('{')
        ) {
          try {
            const parsedManagers = JSON.parse(this.driverData.managers);
            if (Array.isArray(parsedManagers)) {
              managerMap = this.processManagersArray(parsedManagers);
            } else {
              managerMap = this.processManagersArray([parsedManagers]);
            }
          } catch (error) {
            managerMap = this.parseManagers(this.driverData.managers);
          }
        } else {
          managerMap = this.parseManagers(this.driverData.managers);
        }
      } else if (Array.isArray(this.driverData.managers)) {
        managerMap = this.processManagersArray(this.driverData.managers);
      }
      // Check for Managers (uppercase 'M') if lowercase didn't work
      else if (typeof this.driverData.Managers === 'string') {
        // Check if it's a JSON string
        if (
          this.driverData.Managers.trim().startsWith('[') ||
          this.driverData.Managers.trim().startsWith('{')
        ) {
          try {
            const parsedManagers = JSON.parse(this.driverData.Managers);
            if (Array.isArray(parsedManagers)) {
              managerMap = this.processManagersArray(parsedManagers);
            } else {
              managerMap = this.processManagersArray([parsedManagers]);
            }
          } catch (error) {
            managerMap = this.parseManagers(this.driverData.Managers);
          }
        } else {
          managerMap = this.parseManagers(this.driverData.Managers);
        }
      } else if (Array.isArray(this.driverData.Managers)) {
        managerMap = this.processManagersArray(this.driverData.Managers);
      }
      // Check for other possible property names
      else if (
        this.driverData.managerList &&
        Array.isArray(this.driverData.managerList)
      ) {
        managerMap = this.processManagersArray(this.driverData.managerList);
      }
      // Check if managers info is nested in driver object
      else if (this.driverData.driver && this.driverData.driver.managers) {
        if (typeof this.driverData.driver.managers === 'string') {
          // Check if it's a JSON string
          if (
            this.driverData.driver.managers.trim().startsWith('[') ||
            this.driverData.driver.managers.trim().startsWith('{')
          ) {
            try {
              const parsedManagers = JSON.parse(this.driverData.driver.managers);
              if (Array.isArray(parsedManagers)) {
                managerMap = this.processManagersArray(parsedManagers);
              } else {
                managerMap = this.processManagersArray([parsedManagers]);
              }
            } catch (error) {
              managerMap = this.parseManagers(this.driverData.driver.managers);
            }
          } else {
            managerMap = this.parseManagers(this.driverData.driver.managers);
          }
        } else if (Array.isArray(this.driverData.driver.managers)) {
          managerMap = this.processManagersArray(this.driverData.driver.managers);
        }
      }

      // Set manager values with more flexible key matching
      this.driverInfoForm
        .get('fleetManager')
        ?.setValue(this.findManagerByTitle(managerMap, 'Fleet Manager') || '');
      this.driverInfoForm
        .get('safetyManager')
        ?.setValue(this.findManagerByTitle(managerMap, 'Safety Manager') || '');
      this.driverInfoForm
        .get('safetyTrainer')
        ?.setValue(this.findManagerByTitle(managerMap, 'Safety Trainer') || '');
    } else {
      this.driverInfoForm.get('cellphone')?.reset();
      this.driverInfoForm.get('driverName')?.reset();
      this.driverInfoForm.get('driverId')?.reset();
      this.driverInfoForm.get('driverCode')?.reset();
      this.driverInfoForm.get('fleetManager')?.reset();
      this.driverInfoForm.get('safetyManager')?.reset();
      this.driverInfoForm.get('safetyTrainer')?.reset();
      this.driverInfoForm.get('terminal')?.reset();
      this.driverInfoForm.get('driverHireDate')?.reset();
      this.driverData = undefined;
    }
  }

  // Helper method to process managers array
  private processManagersArray(managers: any[]): { [key: string]: string } {
    const managerMap: { [key: string]: string } = {};

    managers.forEach((manager: any) => {
      // Handle different possible property names
      const title = manager.title || manager.role || manager.position || manager.type;
      const name =
        manager.managerName || manager.name || manager.manager || manager.fullName;

      if (title && name) {
        managerMap[title] = name;
      }
    });

    return managerMap;
  }

  // Helper method to find manager by title with flexible matching
  private findManagerByTitle(
    managerMap: { [key: string]: string },
    targetTitle: string
  ): string {
    // First try exact match
    if (managerMap[targetTitle]) {
      return managerMap[targetTitle];
    }

    // Try case-insensitive match
    const lowerTargetTitle = targetTitle.toLowerCase();
    for (const [title, name] of Object.entries(managerMap)) {
      if (title.toLowerCase() === lowerTargetTitle) {
        return name;
      }
    }

    // Try partial match (e.g., "Fleet" matches "Fleet Manager")
    const targetWords = lowerTargetTitle.split(' ');
    for (const [title, name] of Object.entries(managerMap)) {
      const titleWords = title.toLowerCase().split(' ');
      const hasAllWords = targetWords.every((word) =>
        titleWords.some((titleWord) => titleWord.includes(word))
      );
      if (hasAllWords) {
        return name;
      }
    }

    return '';
  }

  getImageUrl(driverId: string): string {
    return this.mediaService.getDriverImageURL(driverId);
  }

  capitalizeWords(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }
}
